import React from 'react';
import { ExternalLink, Code, Smartphone, Gamepad2, GraduationCap, Briefcase } from 'lucide-react';
import { useExperience } from '../../../context/ExperienceContext';

const AboutContent = () => {
  const { yearsOfExperience } = useExperience();
  const { years } = yearsOfExperience;

  return (
    <div className="space-y-6">
      {/* Education */}
      <div className="flex items-start space-x-4">
        <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
          <GraduationCap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Educational Background
          </h3>
          <p className="text-gray-600 dark:text-gray-300">
            I am a graduate of <span className="font-semibold text-blue-600 dark:text-blue-400">BS Information Technology</span> from 
            <span className="font-semibold"> Central Mindanao University</span>, where I built a solid foundation in computer science and software development.
          </p>
        </div>
      </div>

      {/* Experience */}
      <div className="flex items-start space-x-4">
        <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
          <Briefcase className="w-6 h-6 text-green-600 dark:text-green-400" />
        </div>
        <div >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Professional Journey
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-3">
            I have been a <span className="font-semibold text-green-600 dark:text-green-400">full stack developer for {years} year{years !== 1 ? 's' : ''}</span> now, 
            working on diverse projects that have shaped my expertise in both frontend and backend technologies.
          </p>
          
          {/* Company Links */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <span className="text-gray-600 dark:text-gray-300">Co-founder at</span>
              <a 
                href="https://withkm.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group inline-flex items-center space-x-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-3 py-1.5 rounded-lg font-semibold text-sm hover:from-purple-600 hover:to-blue-600 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span>withKM</span>
                <ExternalLink className="w-4 h-4 group-hover:rotate-12 transition-transform" />
              </a>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-gray-600 dark:text-gray-300">Mobile Developer at</span>
              <a 
                href="https://www.wealthgambit.media/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group inline-flex items-center space-x-1 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1.5 rounded-lg font-semibold text-sm hover:from-orange-600 hover:to-red-600 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <span>WG Media</span>
                <ExternalLink className="w-4 h-4 group-hover:rotate-12 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Specializations */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow">
          <div className="flex items-center space-x-3 mb-2">
            <Code className="w-5 h-5 text-blue-500" />
            <h4 className="font-semibold text-gray-900 dark:text-white">Web Applications</h4>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Building scalable and responsive web applications with modern frameworks and technologies.
          </p>
        </div>
        
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow">
          <div className="flex items-center space-x-3 mb-2">
            <Smartphone className="w-5 h-5 text-green-500" />
            <h4 className="font-semibold text-gray-900 dark:text-white">Mobile Applications</h4>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Creating cross-platform mobile solutions that deliver excellent user experiences.
          </p>
        </div>
      </div>

      {/* Passion */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 p-6 rounded-lg border border-purple-200 dark:border-purple-700">
        <div className="flex items-center space-x-3 mb-3">
          <Gamepad2 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Passionate About Game Development
          </h3>
        </div>
        <p className="text-gray-700 dark:text-gray-300">
          Beyond web and mobile development, I'm deeply passionate about <span className="font-semibold text-purple-600 dark:text-purple-400">game development</span>. 
          I love exploring the creative and technical challenges that come with building interactive experiences and bringing ideas to life through code.
        </p>
      </div>

      {/* Personal Touch */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
        <p className="text-gray-700 dark:text-gray-300 italic">
          "I believe that great software is built with passion, attention to detail, and a deep understanding of user needs. 
          Every project is an opportunity to learn something new and create something meaningful."
        </p>
      </div>
    </div>
  );
};

export default AboutContent;